import React, { useState, useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import './App.css';
import axios from 'axios';
import Callback from './Callback';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Types that should use selection boxes
const SELECTION_BOX_TYPES = ['MARCA', 'FUNIL', 'RECENCIA', 'INDEXADOR', 'URL'];

// Order of types
const TYPE_ORDER = [
  'MARCA',
  'FUNIL',
  'RECENCIA',
  'INDEXADOR',
  'URL',
  'SOURCE',
  'MEDIUM',
  'TIPO DE CAMPANHA',
  'TIPO DO ATIVO',
  'PRODUTO',
  'OTIMIZADOR',
  'TIPO DE SEGMENTAÇÃO',
  'SEGMENTO',
  'FORMATO',
  'ATIVO DA IMAGEM',
  'TESE DO ANÚNCIO',
  'MES DE PUBLICACAO',
  'ANO DE PUBLICACAO',
  'VERSAO DA IMAGEM'
];

// Types that should be sorted numerically (ascending)
const NUMERIC_SORT_TYPES = ['ANO DE PUBLICACAO', 'VERSAO DA IMAGEM'];

// Fixed display order for months (only for MES DE PUBLICACAO)
const MONTH_ORDER = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

// Default values
const DEFAULT_VALUES = {
  'MARCA': 'GCB',
  'RECENCIA': 'AON',
  'INDEXADOR': 'NONE'
};

// Type mappings for CSV (some types have different names in CSV)
const TYPE_MAPPING = {
  'MES DE PUBLICACAO': 'MES DE PUBLICACAO DO ANUNCIO',
  'ANO DE PUBLICACAO': 'ANO DE PUBLICACAO DO ANUNCIO'
};

// Componente original da aplicação (taxonomia)
function TaxonomyApp() {
  // Usa o mesmo contexto de auth para obter o access token do Cognito
  const auth = useAuth();
  const [taxonomyData, setTaxonomyData] = useState({});
  const [selectedOptions, setSelectedOptions] = useState({});
  const [showManagePage, setShowManagePage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addOptionModal, setAddOptionModal] = useState({ show: false, type: null });
  const [newOptionValue, setNewOptionValue] = useState('');
  const [addError, setAddError] = useState('');

  useEffect(() => {
    loadTaxonomyData();
    // Initialize default values
    setSelectedOptions(DEFAULT_VALUES);
  }, []);

  const loadTaxonomyData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/options`, {
        headers: auth?.user?.access_token
          ? { Authorization: `Bearer ${auth.user.access_token}` }
          : {},
      });
      const data = response.data;
      
      // Reverse mapping: from DB type to display type
      const reverseMapping = {};
      Object.keys(TYPE_MAPPING).forEach(displayType => {
        reverseMapping[TYPE_MAPPING[displayType]] = displayType;
      });
      
      // Group by type
      const grouped = {};
      data.forEach(item => {
        if (!item.deleted_at) {
          // Map DB type to display type if needed
          const displayType = reverseMapping[item.type] || item.type;
          if (!grouped[displayType]) {
            grouped[displayType] = [];
          }
          grouped[displayType].push(item.option);
        }
      });

      // Sort options
      Object.keys(grouped).forEach(type => {
        if (type === 'MES DE PUBLICACAO') {
          const monthRank = new Map(MONTH_ORDER.map((m, idx) => [m, idx]));
          grouped[type].sort((a, b) => {
            const rankA = monthRank.has(a) ? monthRank.get(a) : Number.POSITIVE_INFINITY;
            const rankB = monthRank.has(b) ? monthRank.get(b) : Number.POSITIVE_INFINITY;
            if (rankA !== rankB) return rankA - rankB;
            // Fallback for unexpected values: keep deterministic order
            return String(a).localeCompare(String(b));
          });
        } else if (NUMERIC_SORT_TYPES.includes(type)) {
          grouped[type].sort((a, b) => {
            if (type === 'VERSAO DA IMAGEM') {
              const numA = parseInt(a.replace('IMG-', ''));
              const numB = parseInt(b.replace('IMG-', ''));
              return numA - numB;
            }
            return parseInt(a) - parseInt(b);
          });
        } else {
          grouped[type].sort();
        }
      });

      setTaxonomyData(grouped);
      setLoading(false);
    } catch (err) {
      const res = err.response;
      const status = res?.status;
      const hdrs = res?.headers || {};
      const requestId =
        hdrs['x-amzn-requestid'] ||
        hdrs['x-amzn-RequestId'] ||
        hdrs['x-request-id'] ||
        hdrs['apigw-requestid'];
      const data = res?.data;
      const bodyStr =
        data == null
          ? ''
          : typeof data === 'string'
            ? data
            : JSON.stringify(data);

      // Inspector: Network tab shows status/body; Console shows full payload + headers
      console.error('[loadTaxonomyData] /api/options failed', {
        url: `${API_BASE_URL}/api/options`,
        status,
        statusText: res?.statusText,
        data,
        headers: res?.headers,
        axiosMessage: err.message,
      });

      let msg = 'Erro ao carregar dados';
      if (status != null) msg += ` (HTTP ${status})`;
      if (requestId) {
        msg += `. ID da requisição (envie ao time com acesso à AWS): ${requestId}`;
      }
      if (bodyStr && bodyStr.length > 0 && bodyStr.length <= 400) {
        msg += ` — ${bodyStr}`;
      } else if (bodyStr && bodyStr.length > 400) {
        msg += ' — corpo da resposta truncado; abra o Console (F12) para o JSON completo.';
      } else if (!res) {
        msg += ': ' + (err.message || 'falha de rede ou bloqueio (CORS)');
      } else if (status === 500 && (!bodyStr || bodyStr.includes('Internal Server Error'))) {
        msg +=
          ' Respostas genéricas do API Gateway costumam indicar timeout ou falha antes do Lambda devolver JSON — o ID acima ajuda a achar o log.';
      }

      setError(msg);
      setLoading(false);
    }
  };

  const handleOptionChange = (type, value) => {
    setSelectedOptions(prev => ({
      ...prev,
      [type]: value
    }));
  };

  const handleAddOption = async () => {
    if (!newOptionValue.trim()) {
      setAddError('Por favor, insira um valor');
      return;
    }

    // Validation: no \ or /
    if (newOptionValue.includes('\\') || newOptionValue.includes('/')) {
      setAddError('Os símbolos \\ e / não são permitidos');
      return;
    }

    // Transform: uppercase, replace _ with -
    const transformedValue = newOptionValue.trim().toUpperCase().replace(/_/g, '-');
    const type = addOptionModal.type;

    try {
      // Check if option already exists
      const existingOptions = taxonomyData[type] || [];
      if (existingOptions.includes(transformedValue)) {
        setAddError('Esta opção já existe para este tipo');
        return;
      }

      // Map display type to DB type if needed
      const dbType = TYPE_MAPPING[type] || type;
      
      await axios.post(
        `${API_BASE_URL}/api/options`,
        {
          type: dbType,
          option: transformedValue,
        },
        {
          headers: auth?.user?.access_token
            ? { Authorization: `Bearer ${auth.user.access_token}` }
            : {},
        }
      );

      // Reload data
      await loadTaxonomyData();
      setAddOptionModal({ show: false, type: null });
      setNewOptionValue('');
      setAddError('');
    } catch (err) {
      if (err.response?.data?.error?.includes('já existe')) {
        setAddError('Esta opção já existe para este tipo');
      } else {
        setAddError('Erro ao adicionar opção: ' + (err.message || 'Erro desconhecido'));
      }
    }
  };

  const handleDeleteOption = async (type, option) => {
    if (!window.confirm(`Tem certeza que deseja excluir "${option}" do tipo "${type}"?`)) {
      return;
    }

    try {
      // Map display type to DB type if needed
      const dbType = TYPE_MAPPING[type] || type;
      
      await axios.delete(`${API_BASE_URL}/api/options`, {
        data: { type: dbType, option },
        headers: auth?.user?.access_token
          ? { Authorization: `Bearer ${auth.user.access_token}` }
          : {},
      });

      // Reload data
      await loadTaxonomyData();
    } catch (err) {
      alert('Erro ao excluir opção: ' + (err.message || 'Erro desconhecido'));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Copiado para a área de transferência!');
    }).catch(err => {
      console.error('Erro ao copiar:', err);
    });
  };

  const generateCampanha = () => {
    const types = ['MARCA', 'SOURCE', 'FUNIL', 'TIPO DE CAMPANHA', 'TIPO DO ATIVO', 'OTIMIZADOR', 'RECENCIA'];
    const values = types.map(type => selectedOptions[type] || '').filter(v => v);
    return values.join('_');
  };

  const generateGrupoAnuncio = () => {
    const types = ['TIPO DE SEGMENTAÇÃO', 'SEGMENTO'];
    const values = types.map(type => selectedOptions[type] || '').filter(v => v);
    return values.join('_');
  };

  const generateAnuncio = () => {
    const types = ['FORMATO', 'ATIVO DA IMAGEM', 'PRODUTO', 'INDEXADOR', 'TESE DO ANÚNCIO', 'MES DE PUBLICACAO', 'ANO DE PUBLICACAO', 'VERSAO DA IMAGEM'];
    const values = types.map(type => selectedOptions[type] || '').filter(v => v);
    return values.join('_');
  };

  const generateUrlTratada = () => {
    const url = selectedOptions['URL'] || '';
    const source = selectedOptions['SOURCE'] || '';
    const medium = selectedOptions['MEDIUM'] || '';
    const campanha = generateCampanha();
    const grupoAnuncio = generateGrupoAnuncio();
    const anuncio = generateAnuncio();
    
    let result = url;
    if (source) result += `?utm_source=${source}`;
    if (medium) result += `&utm_medium=${medium}`;
    if (campanha) result += `&utm_campaign=${campanha}`;
    if (grupoAnuncio) result += `&utm_content=${grupoAnuncio}`;
    if (anuncio) result += `&utm_term=${anuncio}`;
    
    return result.toLowerCase();
  };

  const sortTypes = (types) => {
    return types.sort((a, b) => {
      const indexA = TYPE_ORDER.indexOf(a);
      const indexB = TYPE_ORDER.indexOf(b);
      if (indexA === -1 && indexB === -1) return a.localeCompare(b);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  };

  const renderTypeSelector = (type) => {
    const options = taxonomyData[type] || [];
    const isRadioButton = SELECTION_BOX_TYPES.includes(type);
    const selectedValue = selectedOptions[type] || '';

    if (isRadioButton) {
      return (
        <div className="option-list">
          {options.map(option => (
            <label key={option} className="option-item">
              <input
                type="radio"
                name={type}
                value={option}
                checked={selectedValue === option}
                onChange={(e) => handleOptionChange(type, e.target.value)}
              />
              <span>{option}</span>
            </label>
          ))}
        </div>
      );
    } else {
      return (
        <select
          value={selectedValue}
          onChange={(e) => handleOptionChange(type, e.target.value)}
          className="type-select"
        >
          <option value="">Selecione...</option>
          {options.map(option => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }
  };

  if (showManagePage) {
    const allTypes = sortTypes(Object.keys(taxonomyData));
    return (
      <div className="App">
        <div className="header">
          <div className="header-title">
            <h1>GCB - TAXONOMIA DE MIDIAS</h1>
            <p className="subtitle">v3, 25/03/2026, 9h</p>
          </div>
          <button className="gear-button" onClick={() => setShowManagePage(false)}>
            ← Voltar
          </button>
        </div>
        <div className="manage-container">
          <h2>Gerenciar Opções</h2>
          {allTypes.map(displayType => {
            // Map display type to DB type for delete operation
            const dbType = TYPE_MAPPING[displayType] || displayType;
            return (
              <div key={displayType} className="manage-type-section">
                <h3>{displayType}</h3>
                <div className="manage-options">
                  {(taxonomyData[displayType] || []).map(option => (
                    <div key={option} className="manage-option-item">
                      <span>{option}</span>
                      <button
                        className="delete-button"
                        onClick={() => handleDeleteOption(displayType, option)}
                        title="Excluir"
                      >
                        🗑️
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="App">
        <div className="loading">Carregando...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        <div className="error">{error}</div>
      </div>
    );
  }

  const visibleTypes = sortTypes(TYPE_ORDER.filter(type => taxonomyData[type] && taxonomyData[type].length > 0));

  // Todos os campos visíveis precisam estar selecionados para gerar os resultados
  const allFieldsSelected = visibleTypes.every(type => !!selectedOptions[type]);

  return (
    <div className="App">
      <div className="header">
        <div className="header-title">
          <h1>GCB - TAXONOMIA DE MIDIAS</h1>
          <p className="subtitle">v3, 25/03/2026, 9h</p>
        </div>
        <button className="gear-button" onClick={() => setShowManagePage(true)}>
          ⚙️ Gerenciar Opções
        </button>
      </div>

      <div className="main-container">
        {visibleTypes.map(type => (
          <div key={type} className="type-section">
            <div className="type-header">
              <label className="type-label">{type}</label>
              <button
                className="add-button"
                onClick={() => setAddOptionModal({ show: true, type })}
                title="Adicionar opção"
              >
                +
              </button>
            </div>
            {renderTypeSelector(type)}
          </div>
        ))}
      </div>

      <div className="results-container">
        <h2>Resultados</h2>

        {!allFieldsSelected && (
          <div className="error-message" style={{ marginBottom: '12px' }}>
            Preencha todos os campos para gerar os resultados.
          </div>
        )}
        
        <div className="result-item">
          <label>CAMPANHA:</label>
          <div className="result-value-container">
            <input
              type="text"
              readOnly
              value={allFieldsSelected ? generateCampanha() : ''}
              className="result-input"
            />
            <button
              onClick={() => allFieldsSelected && copyToClipboard(generateCampanha())}
              className="copy-button"
              disabled={!allFieldsSelected}
            >
              📋 Copiar
            </button>
          </div>
        </div>

        <div className="result-item">
          <label>GRUPO DE ANUNCIO:</label>
          <div className="result-value-container">
            <input
              type="text"
              readOnly
              value={allFieldsSelected ? generateGrupoAnuncio() : ''}
              className="result-input"
            />
            <button
              onClick={() => allFieldsSelected && copyToClipboard(generateGrupoAnuncio())}
              className="copy-button"
              disabled={!allFieldsSelected}
            >
              📋 Copiar
            </button>
          </div>
        </div>

        <div className="result-item">
          <label>ANUNCIO:</label>
          <div className="result-value-container">
            <input
              type="text"
              readOnly
              value={allFieldsSelected ? generateAnuncio() : ''}
              className="result-input"
            />
            <button
              onClick={() => allFieldsSelected && copyToClipboard(generateAnuncio())}
              className="copy-button"
              disabled={!allFieldsSelected}
            >
              📋 Copiar
            </button>
          </div>
        </div>

        <div className="result-item">
          <label>URL TRATADA:</label>
          <div className="result-value-container">
            <input
              type="text"
              readOnly
              value={allFieldsSelected ? generateUrlTratada() : ''}
              className="result-input"
            />
            <button
              onClick={() => allFieldsSelected && copyToClipboard(generateUrlTratada())}
              className="copy-button"
              disabled={!allFieldsSelected}
            >
              📋 Copiar
            </button>
          </div>
        </div>
      </div>

      {addOptionModal.show && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Adicionar Opção para {addOptionModal.type}</h3>
            <input
              type="text"
              value={newOptionValue}
              onChange={(e) => {
                setNewOptionValue(e.target.value);
                setAddError('');
              }}
              placeholder="Digite a nova opção"
              className="modal-input"
            />
            {addError && <div className="error-message">{addError}</div>}
            <div className="modal-buttons">
              <button onClick={handleAddOption} className="modal-button-primary">
                Adicionar
              </button>
              <button
                onClick={() => {
                  setAddOptionModal({ show: false, type: null });
                  setNewOptionValue('');
                  setAddError('');
                }}
                className="modal-button-secondary"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Wrapper de autenticação usando react-oidc-context (Cognito)
function App() {
  const auth = useAuth();

  // Verificar se está na rota de callback
  const isCallbackRoute = window.location.pathname === '/callback';

  // Se estiver na rota de callback, renderizar o componente Callback
  if (isCallbackRoute) {
    return <Callback />;
  }

  const signOutRedirect = () => {
    const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
    const logoutUri = process.env.REACT_APP_COGNITO_REDIRECT_URI;
    const cognitoDomain = process.env.REACT_APP_COGNITO_AUTHORITY;
    window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(
      logoutUri
    )}`;
  };

  if (auth.isLoading) {
    return <div>Loading...</div>;
  }

  if (auth.error) {
    return <div>Encountering error... {auth.error.message}</div>;
  }

  if (auth.isAuthenticated) {
    return (
      <>
        <div className="auth-bar">
          <span className="auth-user-label">
            Logado como: {auth.user?.profile?.email || 'usuário'}
          </span>
          <button
            onClick={() => auth.removeUser()}
            className="auth-button auth-button-secondary"
          >
            Sair
          </button>
        </div>
        {/* App original abaixo */}
        <TaxonomyApp />
      </>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1 className="auth-title">GCB - TAXONOMIA DE MIDIAS</h1>
        <p className="subtitle">v3, 25/03/2026, 9h</p>
        <p className="auth-description">
          Faça login com sua conta corporativa para acessar o gerador de taxonomias de mídia.
        </p>
        <div className="auth-buttons">
          <button
            onClick={() => auth.signinRedirect()}
            className="auth-button auth-button-primary"
          >
            Entrar
          </button>
          <button
            onClick={() => signOutRedirect()}
            className="auth-button auth-button-secondary"
          >
            Sair
          </button>
        </div>
      </div>
    </div>
  );
}

export default App;